import { FrameRange, Rectangle } from 'content-creation-common/models';
import { getElementTransformAtFrame } from 'content-creation-common/utils/transform';
import { getGuid } from 'content-creation-common/utils/guid';
import {
  createMediaVariantUrl,
  getMediaUrlByMedia,
} from 'content-creation-common/utils/media';
import { media as mediaConstants } from 'content-creation-common/constants';

import { isNil, isNotEmptyString, isNotNil } from './object';
import { ascendingComparator, isEmpty } from './array';
import {
  CONTENT_TYPE,
  DEFAULT_OUTPUT_VIDEO_FILENAME,
  DEV,
  MAX_THUMBNAIL_WIDTH_OR_HEIGHT,
  MEDIA_PROGRESS_STAGE,
  MEDIA_TYPE,
  OUTPUT_MEDIA_TYPES,
  PLAYBACK_FRAMES_PER_SECOND,
} from '../constants';
import {
  DEFAULT_CREATIVE_HEIGHT_PX,
  DEFAULT_CREATIVE_WIDTH_PX,
} from '../features/creative/constants';
import { warn } from '../util/log';
import { isNotNumber, isNumber } from '../util/numbers';
import { appendQueryParams } from './queryString';
import OutputMediaEntityWireObject from '../model/api/wire-objects/OutputMediaEntityWireObject';
import { getProcessingMedia, getUploadStatus } from './messages';

const { GIF } = mediaConstants;

export { createMediaVariantUrl, getMediaUrlByMedia };

export const generateMediaId = () => {
  return `media-${getGuid()}`;
};

export const getMediaTypeForMimeType = (mimeType) => {
  if (!mimeType) {
    return null;
  }

  if (mimeType.startsWith('image')) {
    if (mimeType.startsWith('image/svg')) {
      return MEDIA_TYPE.SVG;
    }

    if (
      mimeType === 'image/png' ||
      mimeType === 'image/jpeg' ||
      mimeType === 'image/gif'
    ) {
      return MEDIA_TYPE.IMAGE;
    }
  } else if (mimeType.startsWith('video')) {
    return MEDIA_TYPE.VIDEO;
  } else if (mimeType.startsWith('audio')) {
    return MEDIA_TYPE.AUDIO;
  }

  return null;
};

/**
 * @param mediaType
 * @returns {string|null}
 */
export const getContentTypeForMediaType = (mediaType) => {
  switch (mediaType) {
    case MEDIA_TYPE.SVG:
      return CONTENT_TYPE.SVG;
    case MEDIA_TYPE.IMAGE:
      return CONTENT_TYPE.IMAGE;
    case MEDIA_TYPE.VIDEO:
      return CONTENT_TYPE.VIDEO;
    case MEDIA_TYPE.AUDIO:
      return CONTENT_TYPE.AUDIO;
    default:
      warn(`No unique content type for media type ${mediaType}`);
      // For example, both CONTENT_TYPE.TEXT and CONTENT_TYPE.SHAPE are valid
      // content types for MEDIA_TYPE.INLINE_SVG.

      return null;
  }
};

export const isMediaAGIF = (mimeType, fileName) => {
  return (
    mimeType === GIF ||
    (isNil(mimeType) && fileName && fileName.endsWith('.gif'))
  );
};

/*
 ?w=1234&h=576  Will keep proportions and size the image to fill as much of the requested size as possible
 ?w=1234  Will make the image 1234px wide, keeping proportions
 ?h=567   Guess :)
 ?w=1234&h=576&mode=fill  Will return a 1234x576 image, NOT keeping proportions.
 */
export const appendThumbnailProperties = (
  url,
  { attachment, contentDisposition, height, templateId, useFill, width },
  retries = 0,
) => {
  const params = {};

  const maxDimension = Math.max(width || 1, height || 1);
  const scale =
    maxDimension < MAX_THUMBNAIL_WIDTH_OR_HEIGHT
      ? 1.0
      : MAX_THUMBNAIL_WIDTH_OR_HEIGHT / maxDimension;

  if (isNotNil(width)) {
    params.w = Math.max(1, Math.floor(width * scale));
  }

  if (isNotNil(height)) {
    params.h = Math.max(1, Math.floor(height * scale));
  }

  if (useFill) {
    params.mode = 'fill';
  }

  if (retries) {
    params.retries = retries;
  }

  if (templateId) {
    params.templateId = templateId;
  }

  if (contentDisposition) {
    params.contentDisposition = contentDisposition;
  }

  return appendQueryParams(url, params);
};

export const getThumbnailUrl = (
  serverMediaId,
  {
    contentDisposition,
    fileName,
    frameNumber,
    height,
    templateId,
    useFill,
    width,
  },
  retries = 0,
) => {
  if (isNil(serverMediaId)) {
    if (DEV) {
      warn('Refusing to generate thumbnail URL without serverMediaId');
    }
    return null;
  }

  let baseUrl = `/api/v1/media/${serverMediaId}`;
  if (isNotNil(frameNumber)) {
    baseUrl = `${baseUrl}/frame/${frameNumber}`;
  } else {
    baseUrl = `${baseUrl}/file`;
    if (isNotNil(fileName)) {
      baseUrl = `${baseUrl}/${encodeURIComponent(fileName)}`;
    }
  }

  return appendThumbnailProperties(
    baseUrl,
    { width, height, useFill, templateId, contentDisposition },
    retries,
  );
};

export const getOutputMediaUrl = (
  outputMediaId,
  fileName = DEFAULT_OUTPUT_VIDEO_FILENAME,
  isDownload,
  createImageUrl = false,
) => {
  if (isNil(outputMediaId)) {
    if (DEV) {
      warn('Refusing to generate output media URL without outputMediaId');
    }
    return null;
  }

  const queryString = isDownload ? '?contentDisposition=ATTACHMENT' : '';
  return createImageUrl
    ? `/api/v1/media/${outputMediaId}/frame/0${queryString}`
    : `/api/v1/media/${outputMediaId}/file/${encodeURIComponent(
        fileName,
      )}${queryString}`;
};

/**
 * Given the width and height of the full res version from the server, return the optimal size.
 * The optimal size is going to be the smallest of 1.5 * the size of our creative, or the max size from the server
 * @param {number} maxWidthFromServer
 * @param {number} maxHeightFromServer
 * @param {Rectangle} creativeRectangle
 * @returns {{width:Number, height:Number}}
 */
export const getOptimalWidthAndHeightForStockMedia = (
  maxWidthFromServer,
  maxHeightFromServer,
  creativeRectangle,
) => {
  if (!Rectangle.isValidRectangle(creativeRectangle)) {
    throw new Error('Creative Rectangle must be provided');
  }

  // Multiplied by 1.5 so the image can be stretched by 50% before it starts pixelating
  // @todo(CONTENT-1033) update to use dimensions from creative
  const maxWidth = creativeRectangle.width * 1.5;
  const maxHeight = creativeRectangle.height * 1.5;

  const canvasRectangle = new Rectangle(0, 0, maxWidth, maxHeight);
  const mediaRectangle = new Rectangle(
    0,
    0,
    maxWidthFromServer,
    maxHeightFromServer,
  );

  // If the media is larger than the max canvas size then scale the media down to fill the 1.5x canvas dimensions
  if (
    mediaRectangle.width > canvasRectangle.width ||
    mediaRectangle.height > canvasRectangle.height
  ) {
    const smallerMediaRectangle = mediaRectangle.scaleToFill(canvasRectangle);
    return {
      width: smallerMediaRectangle.width,
      height: smallerMediaRectangle.height,
    };
  }

  // Otherwise just return the medias original dimensions
  return {
    width: mediaRectangle.width,
    height: mediaRectangle.height,
  };
};

export function getMediaRatioForBounds(
  element,
  media,
  allowUpscale = false,
  boundsHeight = DEFAULT_CREATIVE_HEIGHT_PX,
  boundsWidth = DEFAULT_CREATIVE_WIDTH_PX,
) {
  if (!element || !media) return 1;

  // Update scale based on previous media size and current size.
  // Get transform at -1
  const { height, width } = media;
  const currentTransform = getElementTransformAtFrame(element, -1, true);
  if (!currentTransform || !height || !width) return 1;

  // check if media is larger than bounds
  let mediaRatioToBounds = 1;
  if (height >= width) {
    // portrait or square
    if (allowUpscale || height > boundsHeight) {
      mediaRatioToBounds = boundsWidth / width;
    }
  } else {
    // landscape
    if (allowUpscale || width > boundsWidth) {
      mediaRatioToBounds = boundsHeight / height;
    }
  }

  return mediaRatioToBounds;
}

export const hasValidImageDimensions = (htmlImage) => {
  const { height, naturalWidth, naturalHeight, width } = htmlImage;
  return (
    // has naturalHeight or height
    ((isNumber(naturalHeight) && naturalHeight > 0) ||
      (isNumber(height) && height > 0)) &&
    // has naturalWidth or width
    ((isNumber(naturalWidth) && naturalWidth > 0) ||
      (isNumber(width) && width > 0))
  );
};

export const hasValidVideoDimensions = (htmlVideoElement) => {
  const { videoHeight, videoWidth } = htmlVideoElement;
  return (
    isNumber(videoHeight) &&
    videoHeight > 0 &&
    isNumber(videoWidth) &&
    videoWidth > 0
  );
};

export const hasValidVideoDuration = (htmlVideoElement) => {
  const { duration } = htmlVideoElement;
  return isNumber(duration) && duration > 0;
};

export const hasValidVideoMetadata = (htmlVideoElement) => {
  return (
    hasValidVideoDimensions(htmlVideoElement) &&
    hasValidVideoDuration(htmlVideoElement)
  );
};

/*
return new Promise((resolve, reject) => {
    // FIXME: Figure out how to appropriately mock this in Jest
    if (TEST) {
      resolve({
        width: 100,
        height: 100,
        duration: 100,
      });

      return;
    }

    let domElement;
    switch (mediaType) {
    case MEDIA_TYPE.VIDEO:
      domElement = document.createElement('video');
      domElement.onloadeddata = () => {
        if (!hasValidVideoMetadata(domElement)) {
          reject(new VideoMetadataError(domElement));
        } else {
          resolve({
            width: domElement.videoWidth,
            height: domElement.videoHeight,
            duration: Math.min(MAX_MEDIA_DURATION_SECONDS, domElement.duration),
          });
        }
      };

      domElement.onerror = () => reject(new Error('Failed to load video', domElement.error));
      domElement.setAttribute('src', url);
      domElement.load();
      break;
    case MEDIA_TYPE.AUDIO:
      domElement = document.createElement('audio');
      domElement.onloadeddata = () => {
        resolve({
          width: null,
          height: null,
          duration: Math.min(MAX_MEDIA_DURATION_SECONDS, domElement.duration),
        });
      };

      domElement.onerror = () => reject(new Error('Failed to load audio', domElement.error));
      domElement.setAttribute('src', url);
      domElement.load();
      break;
    case MEDIA_TYPE.IMAGE:
    case MEDIA_TYPE.SVG:
    case MEDIA_TYPE.INLINE_SVG:
      domElement = new Image();
      domElement.onload = () => {
        resolve({
          width: domElement.naturalWidth || domElement.width,
          height: domElement.naturalHeight || domElement.height,
          duration: null,
        });
      };

      domElement.onerror = () => reject(new Error('Failed to load image', domElement.error));
      domElement.src = url;
      break;
    default:
      throw new Error(`Unexpected media type: ${mediaType}`);
    }
  });
 */

export function getMediaDurationFromFrameCount(frameCount) {
  return frameCount && frameCount > 0
    ? frameCount / PLAYBACK_FRAMES_PER_SECOND
    : 0;
}

export function filterMediaForElementContentType(
  elementContentType,
  mediaList,
) {
  if (isEmpty(mediaList)) return mediaList;
  const isAudioElement = elementContentType === CONTENT_TYPE.AUDIO;
  return mediaList.filter((/* Medium */ media) => {
    const { mediaType } = media;
    if (isNil(mediaType)) return false;

    const contentType = getContentTypeForMediaType(mediaType);
    return isAudioElement
      ? contentType === CONTENT_TYPE.AUDIO
      : contentType !== CONTENT_TYPE.AUDIO;
  });
}

export function getMediaStatusMessage(media, elementName) {
  if (isNil(media)) return elementName;

  const isUploadComplete = media.uploadComplete;
  const uploadedPercent = media.uploadedPercent;

  if (!isUploadComplete) {
    if (uploadedPercent > 0 && uploadedPercent < 1) {
      // show the percentage uploaded
      const message = getUploadStatus(uploadedPercent);
      return `${elementName}: ${message}`;
    } else if (uploadedPercent === 1) {
      // there isn't a good way to show percentage for frame splitting
      // generic message to show that it's still processing even though upload is done
      const message = getProcessingMedia();
      return `${elementName}: ${message}`;
    }
  }

  return elementName;
}

/**
 * Get a matching output media entity for a range that contains a given frame number.
 * @param {Object} creativeMetaData
 * @param {FrameRange} frameRange
 * @returns {OutputMediaEntityWireObject}
 */
export function getOutputMediaEntityForFrameRange(
  creativeMetaData,
  frameRange,
) {
  // TODO check for eventual CreativeMetaData model
  if (isNil(creativeMetaData) || !FrameRange.isFrameRange(frameRange)) {
    return null;
  }
  const { outputMediaEntities, outputMediaId } = creativeMetaData;

  // If somehow entities is empty try returning the old output media id.
  if (isEmpty(outputMediaEntities)) {
    return isNotEmptyString(outputMediaId)
      ? new OutputMediaEntityWireObject(outputMediaId)
      : null;
  }

  // Create list of wire objects.
  const outputMediaEntityWireObjects = outputMediaEntities.map((entity) =>
    OutputMediaEntityWireObject.fromJSON(entity),
  );

  const matchingEntities = outputMediaEntityWireObjects.filter((entity) => {
    const { frameEnd, frameStart } = entity;
    const entityFrameRange = new FrameRange(frameStart, frameEnd);
    return entityFrameRange.contains(frameRange);
  });

  // If there are no entities that match, return old output media id.
  if (isEmpty(matchingEntities)) {
    return isNotEmptyString(outputMediaId)
      ? new OutputMediaEntityWireObject(outputMediaId)
      : null;
  }

  // Sort by frame range length
  matchingEntities.sort((a, b) => {
    const frameRangeA = new FrameRange(a.frameStart, a.frameEnd);
    const frameRangeB = new FrameRange(b.frameStart, b.frameEnd);

    return ascendingComparator(frameRangeA.length, frameRangeB.length);
  });

  return matchingEntities[0];
}

/**
 * Returns all of the output media entites for the full video and the chapters
 * @param {Object} creativeLookup
 * @returns {OutputMediaEntityWireObject[]}
 */
export function getFullVideoAndChapterOutputMediaEntities(creativeLookup) {
  if (isEmpty(creativeLookup.outputMediaEntities)) {
    return null;
  }

  return creativeLookup.outputMediaEntities
    .map((entity) => OutputMediaEntityWireObject.fromJSON(entity))
    .filter((entity) => {
      return (
        entity.outputMediaType === OUTPUT_MEDIA_TYPES.FULL_CREATIVE ||
        entity.outputMediaType === OUTPUT_MEDIA_TYPES.PARTIAL_CREATIVE
      );
    });
}

/**
 * Get a matching output media id for a range that contains a given frame number.
 * @param {Object} creativeMetaData
 * @param {Number} frameNumber
 * @returns {OutputMediaEntityWireObject}
 */
export function getOutputMediaEntityForFrameNumber(
  creativeMetaData,
  frameNumber,
) {
  // TODO check for eventual CreativeMetaData model
  if (isNil(creativeMetaData) || isNotNumber(frameNumber)) return null;
  return getOutputMediaEntityForFrameRange(
    creativeMetaData,
    new FrameRange(frameNumber, frameNumber),
  );
}

/**
 * Get a matching output media id for a range that contains a given FrameRange.
 * @param {Object} creativeMetaData
 * @param {FrameRange} frameRange
 * @returns {String|null}
 */
export function getOutputMediaIdForFrameRange(creativeMetaData, frameRange) {
  const outputMediaEntity = getOutputMediaEntityForFrameRange(
    creativeMetaData,
    frameRange,
  );
  if (isNil(outputMediaEntity)) return null;

  const { outputMediaId: foundOutputMediaId } = outputMediaEntity;
  return foundOutputMediaId;
}

/**
 * Get a matching output media id for a range that contains a frame number.
 * @param {Object} creativeMetaData
 * @param {Number} frameNumber
 * @returns {String|null}
 */
export function getOutputMediaIdForFrameNumber(creativeMetaData, frameNumber) {
  // TODO check for eventual CreativeMetaData model
  if (isNil(creativeMetaData) || isNotNumber(frameNumber)) return null;
  return getOutputMediaIdForFrameRange(
    creativeMetaData,
    new FrameRange(frameNumber, frameNumber),
  );
}

export function getOutputMediaIdForFullCreative(creativeMetaData) {
  // TODO check for eventual CreativeMetaData model
  if (isNil(creativeMetaData)) return null;
  const { outputMediaEntities, outputMediaId } = creativeMetaData;

  // If somehow entities is empty try returning the old output media id.
  if (isEmpty(outputMediaEntities)) {
    return isNotEmptyString(outputMediaId) ? outputMediaId : null;
  }

  // Create list of wire objects.
  const outputMediaEntityWireObjects = outputMediaEntities.map((entity) =>
    OutputMediaEntityWireObject.fromJSON(entity),
  );

  const matchingEntity = outputMediaEntityWireObjects.find((entity) => {
    return entity.outputMediaType === OUTPUT_MEDIA_TYPES.FULL_CREATIVE;
  });

  // If there are no entities that match, return old output media id.
  if (isNil(matchingEntity)) {
    return isNotEmptyString(outputMediaId) ? outputMediaId : null;
  }

  return matchingEntity.outputMediaId;
}

export const isUploading = (uploadingMediaProgress) => {
  if (isNil(uploadingMediaProgress)) return false;
  const { stage } = uploadingMediaProgress;
  return stage === MEDIA_PROGRESS_STAGE.UPLOADING;
};

export const areVideosDoneBuilding = (creativeLookup) => {
  const filteredOutputMediaEntities = getFullVideoAndChapterOutputMediaEntities(
    creativeLookup,
  );
  if (
    isNil(filteredOutputMediaEntities) ||
    isEmpty(filteredOutputMediaEntities)
  ) {
    return false;
  }

  return filteredOutputMediaEntities.every(
    (entity) => entity.videoBuildingComplete,
  );
};
